import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, useEffect, useRef } from 'react';

import { Error } from '../error';
import type { FieldProps } from '../field/field';
import { Hint } from '../hint';
import { Typography } from '../typography';

import styles from './text-view.scss';

const COMPONENT_ID = 'modulor-text-view';

export const TextView: ForwardRefExoticComponent<FieldProps> = forwardRef<
  HTMLDivElement,
  FieldProps
>(
  (
    {
      label,
      value,
      isRequired = false,
      placeholder,
      error,
      disabled = false,
      onChange,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      dataTestId = COMPONENT_ID,
      className,
      hint,
      ...rest
    },
    ref,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const setHeightTextArea = () => {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHeightTextArea();
      onChange && onChange(e.target.value);
    };

    useEffect(() => {
      setHeightTextArea();
    }, [textAreaRef, value]);

    return (
      <div
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        data-testid={dataTestId}
        ref={ref}
        className={classNames(className, styles['text-area-wrapper'])}
        {...rest}>
        <textarea
          ref={textAreaRef}
          className={classNames(styles['body-regular'], {
            [styles['with-label']]: label,
            [styles['with-placeholder']]: placeholder,
            [styles.active]: value,
            [styles.disabled]: disabled,
            [styles.error]: !disabled && error,
          })}
          data-testid={`${dataTestId}-textarea`}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          value={value}></textarea>
        <label
          data-testid={`${dataTestId}-label`}
          className={classNames(styles['label-line'], {
            [styles.active]: value || placeholder,
            [styles.error]: !disabled && error,
            [styles.disabled]: disabled,
          })}>
          <Typography variant="body-regular" className={styles.text}>
            {label}
            {isRequired ? <span className={styles['required-asterisk']}>*</span> : ''}
          </Typography>
        </label>
        {!disabled && typeof error === 'string' ? (
          <Error error={error} data-testid={`${dataTestId}-error`} />
        ) : null}
        {hint && !error ? <Hint hint={hint} dataTestId={`${dataTestId}-hint`} /> : null}
      </div>
    );
  },
);

TextView.displayName = 'TextView';
